// The end-of-session attention capture (migration 0212), shared by the two timing
// engines that can carry it: `habit_sessions` and `routine_sessions`.
//
// It feeds the Mind area's Focus & Attention track (`utils/focusAttention.ts`) and
// nothing else — it mints no XP. The session's own `xp_earned` already credits the
// habit's or routine's domain; crediting it to Mind too would double-count.

import { FOCUS_SCALE } from '../utils/focusAttention';

export interface SessionFocusCapture {
  /** 1–5. See `FOCUS_SCALE` — the scale is owned by the scorer, not restated here. */
  attention: number;
  /** >= 0. How many times attention broke off. */
  distractions: number;
}

/** The capture as PostgREST columns. Clamped at the edge so a bad caller can
 *  never trip 0212's CHECK constraints and fail the whole write. */
export function focusColumns(focus: SessionFocusCapture): { attention: number; distractions: number } {
  return {
    attention: Math.max(FOCUS_SCALE.min, Math.min(FOCUS_SCALE.max, Math.round(focus.attention))),
    distractions: Math.max(0, Math.round(focus.distractions)),
  };
}

/**
 * Is this the "migration 0212 has not been run" error?
 *
 * Postgres `42703` = undefined_column. PostgREST rejects the entire statement when
 * one column is unknown, so callers must be able to retry without the capture
 * rather than lose the session (and its XP) to a rating.
 */
export function isMissingColumn(error: { code?: string; message?: string } | null): boolean {
  if (!error) return false;
  if (error.code === '42703') return true;
  const msg = error.message ?? '';
  return /attention|distractions/.test(msg) && /does not exist/.test(msg);
}

let warned = false;
/** Once per session — this fires on every rated run until 0212 is applied. */
export function warnFocusColumnsMissing(): void {
  if (warned) return;
  warned = true;
  console.warn(
    '[focus] attention capture dropped: habit_sessions/routine_sessions are missing the ' +
      '`attention` + `distractions` columns. Run migration 0212 to start recording it. ' +
      'The session itself completed normally and kept its XP.',
  );
}
